import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchScrapedJobById } from '../store/scrapedJobsSlice';
import { fetchSavedIds, saveJob, unsaveJob } from '../store/savedJobsSlice';
import { RootState, AppDispatch } from '../store';
import { FaMapMarkerAlt, FaClock, FaExternalLinkAlt, FaArrowLeft, FaEnvelope, FaPhone, FaShareAlt, FaTwitter, FaLinkedinIn, FaFacebookF, FaBookmark, FaRegBookmark, FaFlag } from 'react-icons/fa';
import { SkeletonJobDetail } from '../components/Skeleton';
import SEOHead from '../components/SEOHead';
import { getPastTitleDateWarning } from '../utils/listingDateSignals';
import {
  EXTERNAL_APPLICATION_HANDOFF,
  EXTERNAL_EMPLOYER_REVIEW_HANDOFF,
  EXTERNAL_LISTING_TRUST_BOUNDARY,
  getExternalDestinationHost,
} from '../utils/externalListingTrust';
import api from '../services/api';
import { formatCompactJobPay } from '../utils/jobPayPresentation';
import { getJobLocationLabel } from '../utils/jobLocationPresentation';
import { buildJobPostingSalary } from '../utils/jobPayMetadata';
import { buildJobPostingDate } from '../utils/jobPublicationMetadata';
import { getJobSchedulePresentation } from '../utils/jobSchedulePresentation';
import { getOpenActivityCheckedAt, hasEmployerPublishedPay } from '../utils/jobActivityPresentation';
import { formatJobSource } from '../utils/jobSourcePresentation';

const LISTING_REPORT_REASONS = [
  { value: 'suspected_scam', label: 'Suspected scam' },
  { value: 'asks_for_money', label: 'Asks applicants for money' },
  { value: 'impersonation', label: 'Possible company or recruiter impersonation' },
  { value: 'expired_or_filled', label: 'Expired or already filled' },
  { value: 'incorrect_details', label: 'Incorrect listing details' },
  { value: 'broken_application_link', label: 'Broken application link' },
];

const EARLY_EXTERNAL_HANDOFF_DESCRIPTION_LENGTH = 1200;

function formatRate(job: any): string {
  const hasMinimum = job.rate_min !== null && job.rate_min !== undefined;
  const hasMaximum = job.rate_max !== null && job.rate_max !== undefined;
  return hasMinimum || hasMaximum || job.rate
    ? formatCompactJobPay(job)
    : '';
}

function timeAgo(dateStr: string): string {
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
  if (diffHrs < 1) return 'Just now';
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays === 1) return '1 day ago';
  if (diffDays < 7) return `${diffDays} days ago`;
  return new Date(dateStr).toLocaleDateString();
}

function describeApplyMethod(source: string, applyMethod: string): string {
  if (source === 'stagecrew') return 'Direct contact details supplied on StageCrew';
  if (applyMethod === 'email') return 'Email instructions on the original listing';
  if (applyMethod === 'url') return 'Application on the original source website';
  return 'Instructions on the original listing';
}

function hasUsefulDescription(description: unknown, title: unknown): boolean {
  if (typeof description !== 'string' || !description.trim()) return false;
  const normalizedDescription = description.replace(/\s+/g, ' ').trim().toLowerCase();
  const normalizedTitle = typeof title === 'string'
    ? title.replace(/\s+/g, ' ').trim().toLowerCase()
    : '';
  return normalizedDescription !== normalizedTitle;
}

function safeJobsReturnPath(value: unknown): string {
  if (typeof value !== 'string' || (value !== '/jobs' && !value.startsWith('/jobs?'))) {
    return '/jobs';
  }

  try {
    const parsed = new URL(value, 'https://stagecrew.app');
    return parsed.pathname === '/jobs' ? `/jobs${parsed.search}` : '/jobs';
  } catch {
    return '/jobs';
  }
}

const JobDetailsPage = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const { job, loading, jobDetailStatus } = useSelector((state: RootState) => state.scrapedJobs);
  const { accessToken, user } = useSelector((state: RootState) => state.auth);
  const { savedIds, idsLoading } = useSelector((state: RootState) => state.savedJobs);
  const [savePending, setSavePending] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportPending, setReportPending] = useState(false);
  const [reportMessage, setReportMessage] = useState('');
  const [reportError, setReportError] = useState('');
  const [applicationPromptOpen, setApplicationPromptOpen] = useState(false);
  const [applicationPending, setApplicationPending] = useState(false);
  const [applicationMessage, setApplicationMessage] = useState('');
  const [applicationError, setApplicationError] = useState('');
  const applicationPromptRef = useRef<HTMLElement | null>(null);
  const pendingApplicationWindowRef = useRef<Window | null>(null);
  const activeReportJobId = useRef(id);
  const activeApplicationJobId = useRef(id);
  const canSaveJob = user?.role === 'worker';
  const jobsReturnPath = safeJobsReturnPath(
    (location.state as { jobsReturnPath?: unknown } | null)?.jobsReturnPath,
  );

  useEffect(() => {
    if (id) dispatch(fetchScrapedJobById(Number(id)));
  }, [dispatch, id, user?.id, user?.role]);

  useEffect(() => {
    activeReportJobId.current = id;
    activeApplicationJobId.current = id;
    pendingApplicationWindowRef.current?.close();
    pendingApplicationWindowRef.current = null;
    setReportOpen(false);
    setReportReason('');
    setReportPending(false);
    setReportMessage('');
    setReportError('');
    setApplicationPromptOpen(false);
    setApplicationPending(false);
    setApplicationMessage('');
    setApplicationError('');

    return () => {
      // Invalidate every in-flight response on route change or unmount. A
      // reserved blank application tab must never outlive this job-details
      // boundary and later navigate after the user has left.
      activeReportJobId.current = undefined;
      activeApplicationJobId.current = undefined;
      pendingApplicationWindowRef.current?.close();
      pendingApplicationWindowRef.current = null;
    };
  }, [id]);

  useEffect(() => {
    if (!applicationPromptOpen) return undefined;

    const focusPrompt = () => {
      if (document.visibilityState !== 'visible') return;
      window.requestAnimationFrame(() => {
        applicationPromptRef.current?.focus();
        applicationPromptRef.current?.scrollIntoView?.({ block: 'center' });
      });
    };

    document.addEventListener('visibilitychange', focusPrompt);
    window.addEventListener('focus', focusPrompt);
    focusPrompt();
    return () => {
      document.removeEventListener('visibilitychange', focusPrompt);
      window.removeEventListener('focus', focusPrompt);
    };
  }, [applicationPromptOpen]);

  useEffect(() => {
    if (canSaveJob) dispatch(fetchSavedIds());
  }, [canSaveJob, dispatch]);

  // All hooks must be called before any conditional returns
  const jobPostingJsonLd = useMemo(() => {
    if (!job) return null;
    const employmentType = job.employment_type || job.job_type;
    const ld: Record<string, any> = {
      '@context': 'https://schema.org',
      '@type': 'JobPosting',
      title: job.title,
      description: job.description || job.title,
      employmentType: employmentType === 'full_time'
        ? 'FULL_TIME'
        : employmentType === 'part_time'
          ? 'PART_TIME'
          : 'CONTRACTOR',
      hiringOrganization: job.company ? {
        '@type': 'Organization',
        name: job.company,
      } : undefined,
      jobLocation: job.location ? {
        '@type': 'Place',
        address: { '@type': 'PostalAddress', addressLocality: job.location },
      } : undefined,
    };
    const baseSalary = buildJobPostingSalary(job);
    if (baseSalary) ld.baseSalary = baseSalary;
    const datePosted = buildJobPostingDate(job);
    if (datePosted) ld.datePosted = datePosted;
    return ld;
  }, [job]);

  if (loading || jobDetailStatus === 'idle' || jobDetailStatus === 'loading') {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <SkeletonJobDetail />
      </div>
    );
  }

  if (!job && jobDetailStatus === 'error') {
    return (
      <div className="min-h-screen bg-gray-50">
        <SEOHead
          title="Listing temporarily unavailable | StageCrew"
          description="StageCrew could not load this listing right now. Try again or browse currently active AV and stagehand opportunities."
          url={id ? `https://stagecrew.app/jobs/${id}` : 'https://stagecrew.app/jobs'}
          canonical={false}
          robots="noindex, follow"
        />
        <div className="mx-auto flex max-w-3xl items-center px-4 py-16 sm:px-6 lg:px-8">
          <section
            aria-labelledby="job-load-error-heading"
            className="w-full rounded-xl border border-gray-200 bg-white p-8 text-center shadow-sm"
          >
            <p className="mb-3 text-sm font-bold uppercase tracking-wide text-blue-700">
              Temporary problem
            </p>
            <h1 id="job-load-error-heading" className="text-3xl font-bold text-gray-900">We couldn't load this job</h1>
            <p role="alert" className="mx-auto mt-4 max-w-xl text-gray-600">
              The listing may still be available. Try loading it again, or browse other active jobs while
              StageCrew reconnects.
            </p>
            <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
              <button
                type="button"
                onClick={() => id && dispatch(fetchScrapedJobById(Number(id)))}
                className="rounded-lg bg-blue-600 px-6 py-3 font-bold text-white transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Try again
              </button>
              <Link
                to={jobsReturnPath}
                className="rounded-lg border border-gray-300 bg-white px-6 py-3 font-bold text-gray-700 transition hover:bg-gray-50"
              >
                Browse active jobs
              </Link>
            </div>
          </section>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen bg-gray-50">
        <SEOHead
          title="Listing no longer available | StageCrew"
          description="This StageCrew listing is no longer available. Browse currently active AV and stagehand opportunities."
          url={id ? `https://stagecrew.app/jobs/${id}` : 'https://stagecrew.app/jobs'}
          canonical={false}
          robots="noindex, follow"
        />
        <div className="mx-auto flex max-w-3xl items-center px-4 py-16 sm:px-6 lg:px-8">
          <section className="w-full rounded-xl border border-gray-200 bg-white p-8 text-center shadow-sm">
            <p className="mb-3 text-sm font-bold uppercase tracking-wide text-amber-700">
              Listing unavailable
            </p>
            <h1 className="text-3xl font-bold text-gray-900">This job is no longer available</h1>
            <p className="mx-auto mt-4 max-w-xl text-gray-600">
              The employer or original source may have removed or closed it. StageCrew hides unavailable listings
              instead of sending workers to an application that may no longer be open.
            </p>
            <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
              <Link
                to={jobsReturnPath}
                className="rounded-lg bg-blue-600 px-6 py-3 font-bold text-white transition hover:bg-blue-700"
              >
                Browse active jobs
              </Link>
              <Link
                to="/for-workers"
                className="rounded-lg border border-gray-300 bg-white px-6 py-3 font-bold text-gray-700 transition hover:bg-gray-50"
              >
                How StageCrew works
              </Link>
            </div>
            {jobsReturnPath !== '/jobs' && (
              <p className="mt-4 text-sm text-gray-500">
                Your previous job filters will be restored.
              </p>
            )}
          </section>
        </div>
      </div>
    );
  }

  const roles = Array.isArray(job.roles_needed)
    ? job.roles_needed.map((r: string) => r.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()))
    : [];

  const certs = Array.isArray(job.certifications_required) ? job.certifications_required : [];

  const seoTitle = `${job.title}${job.company ? ` at ${job.company}` : ''}${job.location ? ` in ${job.location}` : ''} | StageCrew`;
  const hasDescription = hasUsefulDescription(job.description, job.title);
  const seoDescription = hasDescription
    ? String(job.description).substring(0, 160).replace(/\s+/g, ' ').trim()
    : `${job.title} — review the listing details and original job source on StageCrew.`;
  const sourceLabel = formatJobSource(job.source);
  const sourceDomain = job.source === 'stagecrew'
    ? null
    : getExternalDestinationHost(job.source_url);
  const showMissingDescriptionDisclosure = job.source !== 'stagecrew' && !hasDescription;
  const rateLabel = formatRate(job);
  const openActivityCheckedAt = getOpenActivityCheckedAt(job);
  const canViewApplicationContact = Boolean(user);
  const applicationEmail = canViewApplicationContact
    ? (job.contact_email || job.apply_contact)
    : null;
  const applicationPhone = canViewApplicationContact ? job.contact_phone : null;
  const applicationContactName = canViewApplicationContact ? job.contact_name : null;
  const isSaved = savedIds.includes(job.id);
  const schedulePresentation = getJobSchedulePresentation(job.work_schedule);
  const pastTitleDateWarning = schedulePresentation.cardText
    ? null
    : getPastTitleDateWarning(job.title);
  const saveSignupPath = `/register?${new URLSearchParams({
    role: 'worker',
    redirect: `/jobs/${job.id}`,
  }).toString()}`;
  const reportLoginPath = `/login?${new URLSearchParams({
    redirect: `/jobs/${job.id}`,
  }).toString()}`;
  const canTrackExternalApplication = user?.role === 'worker' && Boolean(accessToken);
  const isEmployerViewer = user?.role === 'employer';
  const showEarlyExternalHandoff = job.source !== 'stagecrew'
    && Boolean(job.source_url)
    && hasDescription
    && String(job.description).replace(/\s+/g, ' ').trim().length >= EARLY_EXTERNAL_HANDOFF_DESCRIPTION_LENGTH;

  const handleToggleSave = async () => {
    setSaveError('');
    setSavePending(true);
    try {
      if (isSaved) {
        await dispatch(unsaveJob(job.id)).unwrap();
      } else {
        await dispatch(saveJob(job.id)).unwrap();
      }
    } catch {
      setSaveError(isSaved ? 'Could not remove this saved job. Try again.' : 'Could not save this job. Try again.');
    } finally {
      setSavePending(false);
    }
  };

  const handleReportSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!reportReason || reportPending) return;

    const submittedRouteId = id;
    setReportPending(true);
    setReportError('');
    setReportMessage('');
    try {
      const response = await api.post(`/scraped-jobs/${job.id}/report`, {
        reason: reportReason,
      });
      if (activeReportJobId.current === submittedRouteId) {
        setReportMessage(response.data.message || 'Thanks. StageCrew will review this listing.');
      }
    } catch {
      if (activeReportJobId.current === submittedRouteId) {
        setReportError('Could not submit this report. Please try again.');
      }
    } finally {
      if (activeReportJobId.current === submittedRouteId) {
        setReportPending(false);
      }
    }
  };

  const handleOpenExternalApplication = async () => {
    if (applicationPending) return;
    const submittedRouteId = id;
    const safeDestination = (value: unknown) => {
      if (typeof value !== 'string') return null;
      try {
        const parsed = new URL(value);
        if (
          !['http:', 'https:'].includes(parsed.protocol)
          || parsed.username
          || parsed.password
        ) {
          return null;
        }
        return parsed.toString();
      } catch {
        return null;
      }
    };

    setApplicationError('');
    setApplicationMessage('');
    setApplicationPromptOpen(false);
    const applicationWindow = window.open('about:blank', '_blank');
    if (!applicationWindow) {
      setApplicationError('Your browser blocked the application tab. Allow pop-ups for StageCrew and try again.');
      return;
    }
    applicationWindow.opener = null;
    pendingApplicationWindowRef.current = applicationWindow;

    setApplicationPending(true);
    try {
      const response = await api.post(`/scraped-jobs/${job.id}/application-opened`);
      if (activeApplicationJobId.current !== submittedRouteId) {
        applicationWindow.close();
        return;
      }
      const redirectUrl = safeDestination(response.data?.redirectUrl);
      if (!redirectUrl) throw new Error('Unsafe application destination');

      pendingApplicationWindowRef.current = null;
      applicationWindow.location.replace(redirectUrl);
      if (response.data?.opened === true && response.data?.confirmationAvailable !== false) {
        setApplicationPromptOpen(true);
      } else {
        setApplicationMessage('Application link opened. Progress confirmation is unavailable right now.');
      }
    } catch (error: any) {
      if (activeApplicationJobId.current === submittedRouteId) {
        const responseStatus = error?.response?.status;
        // Only the server's listing/destination trust boundary can veto the
        // already-rendered safe URL. Session expiry, role drift, rate limits,
        // transport errors, and telemetry outages must not block the primary
        // external application handoff or imply that progress was recorded.
        const serverRejectedHandoff = responseStatus === 404 || responseStatus === 422;
        const fallbackUrl = safeDestination(job.source_url);
        if (fallbackUrl && !serverRejectedHandoff) {
          pendingApplicationWindowRef.current = null;
          applicationWindow.location.replace(fallbackUrl);
          setApplicationMessage('Application link opened. StageCrew could not save progress for this handoff.');
        } else {
          applicationWindow.close();
          setApplicationError('Could not open the application link. Please try again.');
        }
      } else {
        applicationWindow.close();
      }
    } finally {
      if (pendingApplicationWindowRef.current === applicationWindow) {
        pendingApplicationWindowRef.current = null;
      }
      if (activeApplicationJobId.current === submittedRouteId) {
        setApplicationPending(false);
      }
    }
  };

  const handleConfirmExternalApplication = async () => {
    if (applicationPending) return;
    const submittedRouteId = id;
    setApplicationPending(true);
    setApplicationError('');
    try {
      await api.post(`/scraped-jobs/${job.id}/confirm-applied`);
      if (activeApplicationJobId.current === submittedRouteId) {
        setApplicationPromptOpen(false);
        setApplicationMessage('Thanks — your application was marked as submitted.');
      }
    } catch {
      if (activeApplicationJobId.current === submittedRouteId) {
        setApplicationError('Could not confirm your application. Please try again.');
      }
    } finally {
      if (activeApplicationJobId.current === submittedRouteId) {
        setApplicationPending(false);
      }
    }
  };

  const renderExternalApplicationHandoff = (disclosureId: string) => (
    <>
      {canTrackExternalApplication ? (
        <button
          type="button"
          onClick={handleOpenExternalApplication}
          disabled={applicationPending}
          aria-describedby={`${disclosureId} external-listing-trust-boundary`}
          className="inline-flex min-h-11 items-center bg-blue-600 text-white px-8 py-3 rounded-lg font-bold text-lg hover:bg-blue-700 transition disabled:cursor-not-allowed disabled:opacity-60"
        >
          {applicationPending && !applicationPromptOpen ? 'Opening…' : `Continue on ${sourceLabel}`}
          <FaExternalLinkAlt className="ml-2" aria-hidden="true" />
        </button>
      ) : (
        <a
          href={job.source_url}
          target="_blank"
          rel="noopener noreferrer"
          aria-describedby={`${disclosureId} external-listing-trust-boundary`}
          className="inline-flex min-h-11 items-center bg-blue-600 text-white px-8 py-3 rounded-lg font-bold text-lg hover:bg-blue-700 transition"
        >
          {isEmployerViewer ? `View original listing on ${sourceLabel}` : `Continue on ${sourceLabel}`}{' '}
          <FaExternalLinkAlt className="ml-2" aria-hidden="true" />
        </a>
      )}
      <p id={disclosureId} className="text-sm text-gray-500 mt-3">
        {sourceDomain ? `Opens ${sourceDomain}` : 'Opens the original website'} in a new tab.{' '}
        {isEmployerViewer ? EXTERNAL_EMPLOYER_REVIEW_HANDOFF : EXTERNAL_APPLICATION_HANDOFF}
      </p>
      {applicationError && (
        <p className="mt-3 text-sm font-medium text-red-700" role="alert">
          {applicationError}
        </p>
      )}
      {applicationMessage && (
        <p className="mt-3 text-sm font-medium text-green-700" role="status">
          {applicationMessage}
        </p>
      )}
      {applicationPromptOpen && (
        <section
          ref={applicationPromptRef}
          tabIndex={-1}
          aria-labelledby="application-confirmation-heading"
          className="mt-5 max-w-xl rounded-lg border border-blue-200 bg-blue-50 p-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <h3 id="application-confirmation-heading" className="font-bold text-gray-900">
            Did you submit your application?
          </h3>
          <p className="mt-1 text-sm text-gray-700">
            Choose yes only after you completed and submitted the application on {sourceLabel}.
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={handleConfirmExternalApplication}
              disabled={applicationPending}
              className="min-h-11 rounded-lg bg-blue-600 px-4 py-2 font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {applicationPending ? 'Confirming…' : 'Yes, I submitted it'}
            </button>
            <button
              type="button"
              onClick={() => {
                setApplicationPromptOpen(false);
                setApplicationError('');
              }}
              disabled={applicationPending}
              className="min-h-11 rounded-lg border border-gray-300 bg-white px-4 py-2 font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Not yet
            </button>
          </div>
        </section>
      )}
    </>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        url={`https://stagecrew.app/jobs/${job.id}`}
        type="website"
        jsonLd={jobPostingJsonLd}
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to={jobsReturnPath} className="mb-6 inline-flex min-h-11 items-center text-blue-600 hover:underline">
          <FaArrowLeft className="mr-2" /> Back to Jobs
        </Link>

        <div className={`rounded-xl shadow p-5 sm:p-8 ${
          job.is_featured
            ? 'bg-amber-50 border-2 border-amber-400 ring-1 ring-amber-200'
            : 'bg-white'
        }`}>
          {/* Header */}
          <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-start mb-6">
            <div className="min-w-0 flex-1">
              {job.is_featured && (
                <span className="mb-2 inline-flex rounded-full bg-amber-200 px-2.5 py-1 text-xs font-bold text-amber-900">
                  Featured listing
                </span>
              )}
              <h1 className="text-3xl font-bold mb-2">{job.title}</h1>
              <p className={`text-lg font-medium ${job.company ? 'text-gray-800' : 'text-gray-500'}`}>
                {job.company || 'Employer not listed by source'}
              </p>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-gray-600 mt-2">
                <span className="flex items-center">
                  <FaMapMarkerAlt className="mr-1" />{' '}
                  {getJobLocationLabel(job)}
                </span>
                <span className="flex items-center">
                  <FaClock className="mr-1" />{' '}
                  {openActivityCheckedAt ? (
                    <>Applications listed as open · Checked {timeAgo(openActivityCheckedAt)}</>
                  ) : (
                    <>
                      {job.source === 'stagecrew'
                        ? `Posted on StageCrew ${timeAgo(job.scraped_at)}`
                        : 'Current-source check unavailable'}
                    </>
                  )}
                </span>
              </div>
            </div>
            <div className="flex max-w-full flex-col items-start gap-3 sm:ml-4 sm:items-end">
              <div className="max-w-full text-left sm:text-right">
                <div className={rateLabel ? 'text-2xl font-bold text-green-600' : 'sm:max-w-40 text-sm text-gray-500'}>
                  {rateLabel || 'Pay qualification unavailable'}
                </div>
                {rateLabel && hasEmployerPublishedPay(job) && (
                  <div className="mt-1 text-xs font-medium text-gray-600">
                    Pay published by employer
                  </div>
                )}
              </div>
              {canSaveJob && (
                <button
                  type="button"
                  onClick={handleToggleSave}
                  disabled={idsLoading || savePending}
                  aria-pressed={isSaved}
                  className="inline-flex min-h-11 items-center rounded-lg border border-blue-200 bg-white px-3 py-2 text-sm font-semibold text-blue-700 transition hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isSaved ? <FaBookmark className="mr-2" /> : <FaRegBookmark className="mr-2" />}
                  {savePending ? (isSaved ? 'Removing…' : 'Saving…') : isSaved ? 'Saved' : 'Save job'}
                </button>
              )}
              {!user && (
                <div className="flex flex-col items-start sm:items-end">
                  <Link
                    to={saveSignupPath}
                    className="inline-flex min-h-11 items-center rounded-lg border border-blue-200 bg-white px-3 py-2 text-sm font-semibold text-blue-700 transition hover:bg-blue-50"
                  >
                    <FaRegBookmark className="mr-2" />
                    Save job
                  </Link>
                  <span className="mt-1 text-xs text-gray-500">Free worker account required</span>
                </div>
              )}
            </div>
          </div>
          {saveError && (
            <p className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
              {saveError}
            </p>
          )}
          {pastTitleDateWarning && (
            <p
              className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-900"
              role="status"
            >
              Date check: {pastTitleDateWarning}
            </p>
          )}

          {/* Tags */}
          <div className="flex flex-wrap gap-2 mb-6">
            {roles.map((role: string, i: number) => (
              <span key={i} className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm font-medium">{role}</span>
            ))}
            {job.job_type && job.job_type !== 'other' && (
              <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                {job.job_type.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}
              </span>
            )}
            {certs.map((cert: string, i: number) => (
              <span key={`cert-${i}`} className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-medium">{cert}</span>
            ))}
            {job.union_required && job.union_required !== 'either' && (
              <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-medium">
                {job.union_required === 'iatse' ? 'IATSE' : 'Non-Union'}
              </span>
            )}
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
              job.source === 'linkedin' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
            }`}>
              {sourceLabel}
            </span>
          </div>

          <section className="mb-8 rounded-lg border border-blue-100 bg-blue-50 p-4" aria-labelledby="listing-details-heading">
            <h2 id="listing-details-heading" className="mb-3 text-lg font-bold text-gray-900">
              Listing details
            </h2>
            <dl className="grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <dt className="font-semibold text-gray-700">Original source</dt>
                <dd className="text-gray-600">{sourceLabel}</dd>
              </div>
              {sourceDomain && (
                <div>
                  <dt className="font-semibold text-gray-700">Original website</dt>
                  <dd className="break-all text-gray-600">{sourceDomain}</dd>
                </div>
              )}
              <div>
                <dt className="font-semibold text-gray-700">Freshness</dt>
                <dd className="text-gray-600">
                  {openActivityCheckedAt ? (
                    <>
                      <span className="block font-medium text-green-700">Applications listed as open</span>
                      <span className="block">Checked {timeAgo(openActivityCheckedAt)}</span>
                    </>
                  ) : (
                    <>
                      {job.source === 'stagecrew'
                        ? `Posted ${timeAgo(job.scraped_at)}`
                        : 'Current-source check unavailable'}
                    </>
                  )}
                </dd>
              </div>
              <div>
                <dt className="font-semibold text-gray-700">Application method</dt>
                <dd className="text-gray-600">{describeApplyMethod(job.source, job.apply_method)}</dd>
              </div>
              <div>
                <dt className="font-semibold text-gray-700">Schedule</dt>
                <dd className={schedulePresentation.caution ? 'font-medium text-amber-800' : 'text-gray-600'}>
                  {schedulePresentation.detailText}
                </dd>
              </div>
            </dl>
            {job.source !== 'stagecrew' && (
              <div className="mt-3 space-y-1 text-xs text-gray-600">
                <p>
                  {openActivityCheckedAt
                    ? 'The original employer board listed applications as open when StageCrew last checked. Availability can change; the original listing controls.'
                    : 'StageCrew could not confirm that this external listing still meets the current-source requirement.'}
                </p>
                <p id="external-listing-trust-boundary">{EXTERNAL_LISTING_TRUST_BOUNDARY}</p>
              </div>
            )}
          </section>

          {showEarlyExternalHandoff && (
            <section
              aria-labelledby="early-external-application-heading"
              className="mb-8 rounded-lg border border-gray-200 bg-gray-50 p-4"
            >
              <h2 id="early-external-application-heading" className="mb-1 text-lg font-bold text-gray-900">
                Original application link
              </h2>
              <p className="mb-4 text-sm text-gray-600">The full listing description continues below.</p>
              {renderExternalApplicationHandoff('early-external-application-disclosure')}
            </section>
          )}

          {/* Description */}
          {hasDescription && (
            <div className="mb-8">
              <h2 className="text-xl font-bold mb-3">Description</h2>
              <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{job.description}</p>
            </div>
          )}
          {showMissingDescriptionDisclosure && (
            <section
              aria-labelledby="missing-description-heading"
              className="mb-8 rounded-lg border border-amber-200 bg-amber-50 p-4"
            >
              <h2 id="missing-description-heading" className="font-bold text-amber-900">
                Full description not provided
              </h2>
              <p className="mt-1 text-sm text-amber-800">
                The original source did not provide full duties or requirements to StageCrew.
                Review them on {sourceLabel} before applying.
              </p>
            </section>
          )}

          {/* CTA */}
          <div className={showEarlyExternalHandoff ? 'mt-6' : 'border-t border-gray-200 pt-6 mt-6'}>
            {job.source === 'stagecrew' ? (
              <div>
                <h3 className="text-lg font-bold mb-3">Apply Directly</h3>
                <div className="flex flex-wrap gap-3">
                  {applicationEmail && (
                    <a
                      href={`mailto:${applicationEmail}?subject=Application: ${encodeURIComponent(job.title)}`}
                      className="inline-flex items-center bg-blue-600 text-white px-8 py-3 rounded-lg font-bold text-lg hover:bg-blue-700 transition"
                    >
                      <FaEnvelope className="mr-2" /> Apply via Email
                    </a>
                  )}
                  {applicationPhone && (
                    <a
                      href={`tel:${applicationPhone}`}
                      className="inline-flex items-center bg-gray-100 text-gray-800 px-6 py-3 rounded-lg font-medium hover:bg-gray-200 transition"
                    >
                      <FaPhone className="mr-2" /> {applicationPhone}
                    </a>
                  )}
                </div>
                {!applicationEmail && !applicationPhone && (
                  user && accessToken ? (
                    <p className="font-medium text-amber-800">
                      Application contact details are currently unavailable.
                    </p>
                  ) : (
                    <Link
                      to={`/login?${new URLSearchParams({ redirect: `/jobs/${job.id}` }).toString()}`}
                      className="inline-flex items-center bg-blue-600 text-white px-8 py-3 rounded-lg font-bold text-lg hover:bg-blue-700 transition"
                    >
                      Sign in to view contact and apply
                    </Link>
                  )
                )}
                {applicationContactName && (
                  <p className="text-sm text-gray-600 mt-2">Contact: {applicationContactName}</p>
                )}
                <p className="text-sm text-gray-500 mt-3">
                  This listing was submitted directly to StageCrew. Confirm the job details before sharing sensitive information.
                </p>
              </div>
            ) : (
              <div>
                {showEarlyExternalHandoff ? null : job.source_url ? (
                  renderExternalApplicationHandoff('external-application-disclosure')
                ) : (
                  <p className="font-medium text-amber-800">
                    The original application link is unavailable. Search {sourceLabel} for the current listing.
                  </p>
                )}
              </div>
            )}

            <section className="mt-8 border-t border-gray-200 pt-6" aria-label="Listing safety">
              <button
                type="button"
                onClick={() => {
                  setReportOpen(open => !open);
                  setReportError('');
                }}
                aria-expanded={reportOpen}
                aria-controls="report-listing-panel"
                className="inline-flex min-h-11 items-center text-sm font-semibold text-gray-600 transition hover:text-red-700"
              >
                <FaFlag className="mr-2" />
                Report listing
              </button>

              {reportOpen && (
                <div
                  id="report-listing-panel"
                  className="mt-4 max-w-xl rounded-lg border border-gray-200 bg-gray-50 p-4"
                >
                  <h3 id="report-listing-heading" className="font-bold text-gray-900">
                    What should StageCrew review?
                  </h3>
                  <p className="mt-1 text-sm text-gray-600">
                    Reports are reviewed by an administrator and do not automatically remove or penalize a listing.
                  </p>

                  {!user || !accessToken ? (
                    <Link
                      to={reportLoginPath}
                      className="mt-4 inline-flex min-h-11 items-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-blue-700"
                    >
                      Sign in to report
                    </Link>
                  ) : reportMessage ? (
                    <p role="status" className="mt-4 rounded border border-green-200 bg-green-50 p-3 text-sm text-green-800">
                      {reportMessage}
                    </p>
                  ) : (
                    <form onSubmit={handleReportSubmit} className="mt-4">
                      <label htmlFor="listing-report-reason" className="block text-sm font-semibold text-gray-700">
                        Reason
                      </label>
                      <select
                        id="listing-report-reason"
                        value={reportReason}
                        onChange={event => setReportReason(event.target.value)}
                        required
                        className="mt-1 min-h-11 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                      >
                        <option value="">Select a reason</option>
                        {LISTING_REPORT_REASONS.map(reason => (
                          <option key={reason.value} value={reason.value}>{reason.label}</option>
                        ))}
                      </select>
                      {reportError && (
                        <p role="alert" className="mt-3 text-sm text-red-700">{reportError}</p>
                      )}
                      <div className="mt-4 flex gap-3">
                        <button
                          type="submit"
                          disabled={!reportReason || reportPending}
                          className="min-h-11 rounded-lg bg-red-700 px-4 py-2 text-sm font-bold text-white transition hover:bg-red-800 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {reportPending ? 'Submitting…' : 'Submit report'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setReportOpen(false)}
                          className="min-h-11 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100"
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              )}
            </section>

            {/* Social sharing */}
            <div className="flex items-center gap-3 mt-6">
              <span className="text-sm text-gray-500 flex items-center"><FaShareAlt className="mr-1" /> Share:</span>
              <a
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(`https://stagecrew.app/jobs/${job.id}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex min-h-11 min-w-11 items-center justify-center rounded text-blue-700 transition hover:text-blue-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                aria-label="Share on LinkedIn"
              >
                <FaLinkedinIn size={20} />
              </a>
              <a
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(`${job.title}${job.company ? ` at ${job.company}` : ''} — View job details on StageCrew`)}&url=${encodeURIComponent(`https://stagecrew.app/jobs/${job.id}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex min-h-11 min-w-11 items-center justify-center rounded text-gray-700 transition hover:text-black focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                aria-label="Share on Twitter"
              >
                <FaTwitter size={20} />
              </a>
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(`https://stagecrew.app/jobs/${job.id}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex min-h-11 min-w-11 items-center justify-center rounded text-blue-600 transition hover:text-blue-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                aria-label="Share on Facebook"
              >
                <FaFacebookF size={20} />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobDetailsPage;
